/**
 * Estado de pago armado desde las guías cargadas.
 * Es el cierre del círculo: nada se transcribe a mano.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock, LockOpen, FileSpreadsheet, TriangleAlert } from 'lucide-react';
import {
  Alerta,
  Boton,
  Campo,
  Chip,
  Indicador,
  Tarjeta,
  TarjetaTitulo,
  Vacio,
} from '@/components/ui';
import type { DatosEP } from '@/db/exportarExcel';
import { listar } from '@/db/repositorio';
import { useCatalogos } from '@/hooks/useCatalogos';
import { useAuth } from '@/hooks/useAuth';
import { useDialogo } from '@/components/ui/dialogo';
import { armarEstadoPago, resumenHes, type GuiaConRecargos } from '@/domain/estadoPago';
import { calcularRecargos } from '@/domain/recargos';
import { calcularTiempos } from '@/domain/tiempos';
import { parsearProducto } from '@/domain/producto';
import { aFechaISO, clp, fecha as ffecha, uf } from '@/domain/formato';
import { controlarContrato } from '@/domain/precios';
import { lazy, Suspense } from 'react';
import { cn } from '@/lib/cn';
import { useGuias } from '@/hooks/useGuias';
import { leerContrato, type DocContrato } from '@/db/contrato';
import {
  acumuladoAntesDe,
  cerrarEstadoPago,
  epDelPeriodo,
  listarEstadosPago,
  problemasParaCerrar,
  reabrirEstadoPago,
  type DocEstadoPago,
} from '@/db/estadosPago';

const ExportarPdf = lazy(() => import('./ExportarPdf'));
const Plantilla = lazy(() => import('./Plantilla'));

function inicioDeMes(): string {
  const d = new Date();
  return aFechaISO(new Date(d.getFullYear(), d.getMonth(), 1));
}

export default function EstadoPago() {
  const { catalogos, buscarItem } = useCatalogos();
  const { perfil, puede } = useAuth();
  const { confirmar, pedirTexto } = useDialogo();
  const { guias } = useGuias();
  const [desde, setDesde] = useState(inicioDeMes());
  const [hasta, setHasta] = useState(aFechaISO(new Date()));
  const [valorUf, setValorUf] = useState('40860,6');
  const [factorGg, setFactorGg] = useState('1');

  // Los montos del contrato son un dato administrado. Sin ellos, el estado de
  // pago no puede declarar cuánto se lleva consumido — y este documento se le
  // manda a CODELCO, así que no se rellena con supuestos.
  const [contratoDoc, setContratoDoc] = useState<DocContrato | null>(null);

  useEffect(() => {
    void leerContrato().then(setContratoDoc);
  }, []);

  /** Estados de pago ya guardados: dan el acumulado anterior y la cadena. */
  const [eps, setEps] = useState<DocEstadoPago[]>([]);
  const [cerrando, setCerrando] = useState(false);
  const [avisoCierre, setAvisoCierre] = useState<string | null>(null);
  const [errorCierre, setErrorCierre] = useState<string[]>([]);

  const recargarEps = useCallback(async () => {
    setEps(await listarEstadosPago());
  }, []);

  useEffect(() => {
    void recargarEps();
  }, [recargarEps]);
  const [expandido, setExpandido] = useState<number | null>(null);
  const [numeroEp, setNumeroEp] = useState('59');
  const [gastosGenerales, setGastosGenerales] = useState<
    {
      grupo: string;
      descripcion: string;
      unidad: string;
      cantidadContratada: number;
      precioUnitarioUf: number;
      orden: number;
    }[]
  >([]);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    void listar<(typeof gastosGenerales)[number] & { id: string }>('gastosGenerales').then((g) =>
      setGastosGenerales(g.sort((a, b) => a.orden - b.orden)),
    );
  }, []);

  const ufNum = Number(valorUf.replace(/\./g, '').replace(',', '.')) || 0;

  /** Recalcula tiempos y recargos de cada guía con el motor de dominio. */
  const guiasConRecargos: GuiaConRecargos[] = useMemo(() => {
    return guias.map((g) => {
      const planta = catalogos.plantas.find((p) => p.codigoPlanta === g.plantaCodigo);
      const producto = catalogos.productos.find((p) => p.id === g.productoId);
      const d = (s: string | null) => (s ? new Date(s) : undefined);

      const tiempos = calcularTiempos({
        salidaPlanta: d(g.tsSalidaPlanta),
        llegadaObra: d(g.tsLlegadaObra),
        inicioDescarga: d(g.tsInicioDescarga),
        terminoDescarga: d(g.tsTerminoDescarga),
        salidaObra: d(g.tsSalidaObra),
        llegadaPlanta: d(g.tsLlegadaPlanta),
      });

      const recargos =
        planta && producto
          ? calcularRecargos(
              {
                plantaCodigo: g.plantaCodigo,
                minCargaM3: planta.minCargaM3,
                cantidadM3: g.cantidadM3,
                producto: parsearProducto(producto.codigo),
                generaRecargos: producto.generaRecargos,
                tiempos,
                codigoProductoNormalizado: producto.codigoNormalizado,
              },
              buscarItem,
            )
          : [];

      return {
        guia: {
          id: g.id,
          fecha: g.fecha,
          cantidadM3: g.cantidadM3,
          itemContratoId: g.itemContratoId,
          estado: g.estado,
        },
        recargos,
      };
    });
  }, [guias, catalogos, buscarItem]);

  const ep = useMemo(
    () =>
      armarEstadoPago({
        periodoDesde: desde,
        periodoHasta: hasta,
        valorUf: ufNum,
        items: catalogos.items,
        capitulos: catalogos.capitulos,
        guias: guiasConRecargos,
        acumuladoAnterior: acumuladoAntesDe(eps, desde),
        factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
      }),
    [desde, hasta, ufNum, catalogos, guiasConRecargos, factorGg, eps],
  );

  /** Guías del período, que son las que entran al cierre. */
  const guiasDelPeriodo = useMemo(
    () => guias.filter((g) => g.fecha >= desde && g.fecha <= hasta && g.estado !== 'ANULADA'),
    [guias, desde, hasta],
  );

  /** Si este período ya se cerró, se muestra lo emitido, no lo que daría hoy. */
  const epCerrado = useMemo(() => epDelPeriodo(eps, desde, hasta), [eps, desde, hasta]);
  const bloqueado = Boolean(epCerrado && epCerrado.estado !== 'ABIERTO');

  /**
   * Lo que se muestra. Un EP cerrado no se recalcula: se lee el documento tal
   * como se emitió, aunque después hayan cambiado precios o guías.
   */
  const epMostrado = useMemo(
    () =>
      bloqueado && epCerrado ? { ...ep, lineas: epCerrado.lineas, totales: epCerrado.totales } : ep,
    [ep, epCerrado, bloqueado],
  );

  const hes = useMemo(
    () => resumenHes(epMostrado, catalogos.capitulos),
    [epMostrado, catalogos.capitulos],
  );
  const contrato = contratoDoc ? controlarContrato({ ...contratoDoc, valorUf: ufNum }) : null;

  async function cerrar() {
    setErrorCierre([]);
    setAvisoCierre(null);

    const numero = Number(numeroEp) || 0;
    const problemas = problemasParaCerrar(
      {
        numero,
        periodoDesde: desde,
        periodoHasta: hasta,
        guiaIds: guiasDelPeriodo.map((g) => g.id),
      },
      eps,
      guiasDelPeriodo.map((g) => ({ id: g.id, estadoPagoId: g.estadoPagoId })),
      epCerrado?.id ?? null,
    );
    if (problemas.length) {
      setErrorCierre(problemas);
      return;
    }

    const si = await confirmar({
      titulo: `Cerrar el EP N° ${numero}`,
      mensaje:
        `Se guardan las ${lineasConMovimiento.length} partidas con movimiento del ${ffecha(desde)} ` +
        `al ${ffecha(hasta)}, con los precios y la UF de hoy, y las ${guiasDelPeriodo.length} guías ` +
        'del período quedan marcadas como facturadas. Desde ahí el EP deja de recalcularse. ' +
        'Se puede reabrir cuando haga falta.',
      aceptar: 'Cerrar el EP',
    });
    if (!si) return;

    setCerrando(true);
    try {
      await cerrarEstadoPago(
        {
          numero,
          periodoDesde: desde,
          periodoHasta: hasta,
          valorUf: ufNum,
          factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
          lineas: ep.lineas,
          totales: ep.totales,
          guiaIds: guiasDelPeriodo.map((g) => g.id),
        },
        perfil?.uid ?? null,
        epCerrado,
      );
      await recargarEps();
      setAvisoCierre(`EP N° ${numero} cerrado.`);
    } catch (e) {
      setErrorCierre([e instanceof Error ? e.message : 'No se pudo cerrar el estado de pago.']);
    } finally {
      setCerrando(false);
    }
  }

  async function reabrir() {
    if (!epCerrado) return;
    const motivo = await pedirTexto({
      titulo: `Reabrir el EP N° ${epCerrado.numero}`,
      mensaje:
        'Vuelve a quedar editable y sus guías dejan de estar marcadas como facturadas. ' +
        'Queda registrado quién lo reabrió y por qué.',
      etiqueta: 'Motivo',
      marcador: 'Ej.: faltaron 3 guías del día 18',
      aceptar: 'Reabrir',
      tono: 'peligro',
    });
    if (!motivo?.trim()) return;

    setCerrando(true);
    setErrorCierre([]);
    try {
      await reabrirEstadoPago(epCerrado, motivo.trim(), perfil?.uid ?? null);
      await recargarEps();
      setAvisoCierre(`EP N° ${epCerrado.numero} reabierto.`);
    } catch (e) {
      setErrorCierre([e instanceof Error ? e.message : 'No se pudo reabrir el estado de pago.']);
    } finally {
      setCerrando(false);
    }
  }

  const lineasConMovimiento = useMemo(
    () => epMostrado.lineas.filter((l) => l.cantPeriodo !== 0),
    [epMostrado.lineas],
  );

  /**
   * Datos del EP, compartidos por el exportador de Excel y el de PDF.
   * Sólo se llama con el contrato ya configurado: los botones que exportan
   * están deshabilitados hasta entonces.
   */
  const datosEp = (): DatosEP => {
    if (!contrato) throw new Error('Falta configurar el contrato en Administración.');
    return {
      numero: Number(numeroEp) || 0,
      periodoDesde: ffecha(desde),
      periodoHasta: ffecha(hasta),
      mesReferencia: ffecha(hasta),
      valorUf: ufNum,
      fechaValorUf: ffecha(hasta),
      lineas: epMostrado.lineas,
      totales: epMostrado.totales,
      capitulos: catalogos.capitulos,
      contrato,
      gastosGenerales,
      factorGastosGenerales: Number(factorGg.replace(',', '.')) || 0,
      guias: guias.filter((g) => g.fecha >= desde && g.fecha <= hasta),
    };
  };

  /** Celdas de entrada del maestro: el resto lo calculan sus propias fórmulas. */
  const datosPlantilla = () => ({
    numeroEp: Number(numeroEp) || 0,
    periodoDesde: desde,
    periodoHasta: hasta,
    mesReferencia: hasta,
    valorUf: ufNum,
    guias: guias
      .filter((g) => g.fecha >= desde && g.fecha <= hasta && g.estado !== 'BORRADOR')
      .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.folio - b.folio),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Estado de pago</h1>
          <p className="text-xs text-texto-mute">
            Armado automáticamente desde las guías del período
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Campo
            etiqueta="N° de EP"
            name="numeroEp"
            inputMode="numeric"
            value={numeroEp}
            onChange={(e) => setNumeroEp(e.target.value.replace(/\D/g, ''))}
            className="w-24 font-mono"
          />
          <Boton
            variante="primario"
            icono={<FileSpreadsheet size={15} />}
            disabled={exportando || !lineasConMovimiento.length || !contrato}
            onClick={() => {
              setExportando(true);
              void import('@/db/exportarExcel')
                .then((m) => m.exportarEstadoPago(datosEp()))
                .finally(() => setExportando(false));
            }}
          >
            {exportando ? 'Generando…' : 'Descargar Excel'}
          </Boton>
        </div>
      </div>

      <Tarjeta>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Campo
            etiqueta="Período desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <Campo
            etiqueta="Período hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <Campo
            etiqueta="Valor UF del cierre"
            inputMode="decimal"
            value={valorUf}
            onChange={(e) => setValorUf(e.target.value)}
            sufijo="$"
            className="font-mono"
            disabled={bloqueado}
          />
          <Campo
            etiqueta="Cuota gastos generales"
            inputMode="decimal"
            value={factorGg}
            onChange={(e) => setFactorGg(e.target.value)}
            sufijo="mes"
            className="font-mono"
            disabled={bloqueado}
          />
        </div>
      </Tarjeta>

      <Tarjeta>
        <TarjetaTitulo
          accion={
            epCerrado ? (
              <Chip nivel={bloqueado ? 'VERDE' : 'AMARILLO'}>
                {bloqueado ? `Cerrado · EP N° ${epCerrado.numero}` : 'Reabierto'}
              </Chip>
            ) : (
              <Chip nivel="AMARILLO">Sin cerrar</Chip>
            )
          }
        >
          Cierre del período
        </TarjetaTitulo>

        <div className="space-y-3 p-4">
          {errorCierre.length > 0 && (
            <ul className="space-y-1 rounded-control border border-fluor-rojo/40 bg-fluor-rojo/10 px-3 py-2 text-xs text-fluor-rojo">
              {errorCierre.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {avisoCierre && (
            <p className="rounded-control border border-fluor-verde/40 bg-fluor-verde/10 px-3 py-2 text-xs text-fluor-verde">
              {avisoCierre}
            </p>
          )}

          {bloqueado && epCerrado ? (
            <>
              <p className="text-xs leading-relaxed text-texto-mute">
                Cerrado el {ffecha((epCerrado.cerradoEn ?? '').slice(0, 10))} con la UF a{' '}
                {clp(epCerrado.valorUf)}. Lo que ves es lo que se emitió: no cambia aunque después
                se corrijan precios o guías. Sus {epCerrado.guiaIds.length.toLocaleString('es-CL')}{' '}
                guías están marcadas como facturadas.
              </p>
              {puede('estadoPago') && (
                <Boton
                  variante="peligro"
                  icono={<LockOpen size={15} />}
                  onClick={() => void reabrir()}
                  disabled={cerrando}
                >
                  Reabrir para corregir
                </Boton>
              )}
            </>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-texto-mute">
                Cerrar guarda este EP tal como está —partidas, precios y UF de hoy— y marca sus{' '}
                {guiasDelPeriodo.length.toLocaleString('es-CL')} guías como facturadas, para que no
                vuelvan a cobrarse en el siguiente. El acumulado anterior del próximo EP sale de
                acá. Se puede reabrir cuando haga falta.
              </p>
              {puede('estadoPago') && (
                <Boton
                  variante="primario"
                  icono={<Lock size={15} />}
                  onClick={() => void cerrar()}
                  disabled={cerrando || !lineasConMovimiento.length}
                >
                  {cerrando ? 'Cerrando…' : `Cerrar el EP N° ${numeroEp || '—'}`}
                </Boton>
              )}
            </>
          )}

          {eps.length > 0 && (
            <div className="border-t border-linea pt-3">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-texto-mute">
                Estados de pago guardados
              </div>
              <div className="flex flex-wrap gap-1.5">
                {eps.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      setDesde(e.periodoDesde);
                      setHasta(e.periodoHasta);
                      setNumeroEp(String(e.numero));
                      setAvisoCierre(null);
                      setErrorCierre([]);
                    }}
                    className="rounded-control border border-linea bg-petroleo-800/40 px-2.5 py-1 text-[11px] text-texto-mute transition-colors hover:bg-white/5 hover:text-texto"
                  >
                    N° {e.numero} · {ffecha(e.periodoDesde)} a {ffecha(e.periodoHasta)} ·{' '}
                    {uf(e.totales?.totalUf ?? 0)} UF
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Tarjeta>

      {contrato ? (
        <Alerta nivel={contrato.nivel} titulo={contrato.mensaje} />
      ) : (
        <Alerta nivel="NEUTRO" titulo="Contrato sin configurar">
          El estado de pago declara cuánto se lleva consumido del contrato, y ese dato todavía no
          está cargado. Se configura en Administración; hasta entonces no se puede exportar.
        </Alerta>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Indicador
          etiqueta="Total del período"
          valor={uf(epMostrado.totales.totalUf)}
          unidad="UF"
          nivel="VERDE"
        />
        <Indicador etiqueta="Neto" valor={clp(epMostrado.totales.totalClp)} />
        <Indicador etiqueta="IVA 19%" valor={clp(epMostrado.totales.ivaClp)} />
        <Indicador
          etiqueta="Total a facturar"
          valor={clp(epMostrado.totales.totalFacturarClp)}
          nivel="VERDE"
        />
      </div>

      {epMostrado.advertencias.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo>Revisar antes de cerrar</TarjetaTitulo>
          <div className="space-y-1.5 p-4">
            {epMostrado.advertencias.map((a) => (
              <div key={a} className="flex items-start gap-2 text-xs text-fluor-amarillo">
                <TriangleAlert size={13} className="mt-0.5 shrink-0" />
                {a}
              </div>
            ))}
          </div>
        </Tarjeta>
      )}

      <Suspense
        fallback={
          <Tarjeta>
            <div className="p-4 text-xs text-texto-mute">Cargando…</div>
          </Tarjeta>
        }
      >
        <Plantilla datos={datosPlantilla} habilitado={lineasConMovimiento.length > 0} />
        <div className="h-4" />
        <ExportarPdf datos={datosEp} habilitado={lineasConMovimiento.length > 0} />
      </Suspense>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Físico-financiero por capítulo */}
        <Tarjeta className="lg:col-span-2">
          <TarjetaTitulo>
            Avance físico-financiero · {ffecha(desde)} a {ffecha(hasta)}
          </TarjetaTitulo>

          {lineasConMovimiento.length === 0 ? (
            <Vacio titulo="Sin movimiento en el período">
              Carga guías con fecha dentro del rango y el estado de pago se arma solo.
            </Vacio>
          ) : (
            <div className="divide-y divide-linea/60">
              {epMostrado.totales.porCapitulo.map((c) => {
                const lineas = lineasConMovimiento.filter(
                  (l) => l.capituloNumero === c.capituloNumero,
                );
                const abierto = expandido === c.capituloNumero;
                return (
                  <div key={c.capituloNumero}>
                    <button
                      type="button"
                      onClick={() => setExpandido(abierto ? null : c.capituloNumero)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[.03]"
                    >
                      <span className="font-mono text-xs tabular-nums text-texto-mute">
                        {c.capituloNumero}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm">{c.nombre}</span>
                      <span className="shrink-0 font-mono text-sm tabular-nums">
                        {uf(c.montoUf)}
                      </span>
                    </button>

                    {abierto && (
                      <div className="bg-petroleo-900/40 px-4 pb-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                              <th className="py-1.5 font-medium">Partida</th>
                              <th className="py-1.5 font-medium">Descripción</th>
                              <th className="py-1.5 text-right font-medium">Cantidad</th>
                              <th className="py-1.5 text-right font-medium">Precio UF</th>
                              <th className="py-1.5 text-right font-medium">Monto UF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineas.map((l) => (
                              <tr key={l.itemContratoId} className="border-t border-linea/40">
                                <td className="py-1.5 pr-2 font-mono tabular-nums">
                                  {l.itemCodigo}
                                </td>
                                <td className="max-w-[16rem] truncate py-1.5 pr-2">
                                  {l.descripcion}
                                </td>
                                <td className="py-1.5 pr-2 text-right font-mono tabular-nums">
                                  {uf(l.cantPeriodo, 2)}
                                </td>
                                <td className="py-1.5 pr-2 text-right font-mono tabular-nums text-texto-mute">
                                  {uf(l.precioUf)}
                                </td>
                                <td className="py-1.5 text-right font-mono tabular-nums">
                                  {uf(l.montoPeriodoUf)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex items-center justify-between bg-petroleo-900/50 px-4 py-3">
                <span className="text-sm font-semibold uppercase tracking-wide">Total del EP</span>
                <span className="font-mono text-lg font-semibold tabular-nums text-fluor-verde">
                  {uf(epMostrado.totales.totalUf)}{' '}
                  <span className="text-xs text-texto-mute">UF</span>
                </span>
              </div>
            </div>
          )}
        </Tarjeta>

        {/* HES */}
        <Tarjeta className="h-fit">
          <TarjetaTitulo>Resumen HES</TarjetaTitulo>
          <div className="divide-y divide-linea/50">
            {hes.filas.map((f) => (
              <div
                key={f.numero}
                className={cn(
                  'flex items-center gap-2 px-4 py-1.5 text-xs',
                  f.montoUf === 0 && 'text-texto-mute/60',
                )}
              >
                <span className="w-5 shrink-0 font-mono tabular-nums text-texto-mute">
                  {f.numero}
                </span>
                <span className="min-w-0 flex-1 truncate">{f.nombre}</span>
                <span className="shrink-0 font-mono tabular-nums">{uf(f.montoUf)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 border-t border-linea px-4 py-3 text-xs">
            <div className="flex justify-between">
              <span className="text-texto-mute">Total UF</span>
              <span className="font-mono tabular-nums">{uf(hes.totalUf)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">Valor UF</span>
              <span className="font-mono tabular-nums">{clp(hes.valorUf)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">Neto</span>
              <span className="font-mono tabular-nums">{clp(hes.totalClp)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-texto-mute">IVA 19%</span>
              <span className="font-mono tabular-nums">{clp(hes.ivaClp)}</span>
            </div>
            <div className="flex justify-between border-t border-linea pt-1.5 font-semibold">
              <span>A facturar</span>
              <span className="font-mono tabular-nums text-fluor-verde">
                {clp(hes.totalFacturarClp)}
              </span>
            </div>
          </div>
        </Tarjeta>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip>{guias.length} guías en el sistema</Chip>
        <Chip nivel={epMostrado.guiasSinPartida.length ? 'AMARILLO' : 'VERDE'}>
          {epMostrado.guiasSinPartida.length} sin partida
        </Chip>
      </div>
    </div>
  );
}
